import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../App';
import { useCurrency } from '../../contexts/CurrencyContext';
import Layout from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import ReviewForm from '../../components/ReviewForm';
import ReviewsList from '../../components/ReviewsList';
import CreateTipForm from '../../components/tips/CreateTipForm';
import TipsTable from '../../components/tips/TipsTable';
import ServiceStats from '../../components/tips/ServiceStats';
import SubscriptionButton from '../../components/tips/SubscriptionButton';
import { ExternalLink, Mail, Phone, Globe, Facebook, Instagram, Twitter, Upload, Trash2, Image as ImageIcon, Video, Heart, Star, Bell, BellOff } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';
import Price from '../../components/Price';
import { trackEvent } from '../../components/GoogleAnalytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ServiceDetail = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const [service, setService] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [uploadData, setUploadData] = useState({
    content: '',
    file: null
  });
  const [liked, setLiked] = useState(false);
  const [following, setFollowing] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [averageRating, setAverageRating] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);
  const [tipsRefresh, setTipsRefresh] = useState(0);

  useEffect(() => {
    // Load all data in parallel for faster page load
    loadAllData();
  }, [serviceId]);

  const loadAllData = async () => {
    try {
      const requests = [
        axios.get(`${API}/tipster-services/${serviceId}`),
        axios.get(`${API}/tipster-services/${serviceId}/posts`),
        axios.get(`${API}/reviews/tipster_service/${serviceId}`)
      ];

      const results = await Promise.allSettled(requests);

      // Handle service data
      if (results[0].status === 'fulfilled') {
        setService(results[0].value.data);
      } else {
        console.error('Error fetching service:', results[0].reason);
        toast.error('Service not found');
        navigate('/tip-services');
      }

      // Handle posts
      if (results[1].status === 'fulfilled') {
        setPosts(results[1].value.data);
      }

      // Handle reviews
      if (results[2].status === 'fulfilled') {
        setReviews(results[2].value.data.reviews);
        setAverageRating(results[2].value.data.average_rating);
        setTotalReviews(results[2].value.data.total_reviews);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Check like and follow status separately whenever user or serviceId changes
  useEffect(() => {
    if (user && serviceId) {
      checkLiked();
      checkFollowStatus();
    }
  }, [user, serviceId]);

  // Refetch follow status when returning to this page
  useEffect(() => {
    if (user && serviceId && location.pathname.includes(serviceId)) {
      checkFollowStatus();
    }
  }, [location.key]);

  const fetchService = async () => {
    try {
      const response = await axios.get(`${API}/tipster-services/${serviceId}`);
      setService(response.data);
    } catch (error) {
      console.error('Error fetching service:', error);
      toast.error('Service not found');
      navigate('/tip-services');
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews/tipster_service/${serviceId}`);
      setReviews(response.data.reviews);
      setAverageRating(response.data.average_rating);
      setTotalReviews(response.data.total_reviews);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const checkLiked = async () => {
    const token = localStorage.getItem('session_token');
    try {
      const response = await axios.get(`${API}/likes/check?item_id=${serviceId}&item_type=tipster_service`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLiked(response.data.liked);
    } catch (error) {
      console.error('Error checking like status:', error);
    }
  };

  const checkFollowStatus = async () => {
    if (!user) return;
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
      });
      const isFollowing = response.data.followed_tip_services?.includes(serviceId) || false;
      setFollowing(isFollowing);
    } catch (error) {
      console.error('Error checking follow status:', error);
    }
  };

  const handleToggleLike = async () => {
    if (!user) {
      toast.error('Please login to like services');
      navigate('/login');
      return;
    }

    const token = localStorage.getItem('session_token');
    try {
      const response = await axios.post(
        `${API}/likes/toggle?item_id=${serviceId}&item_type=tipster_service`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLiked(response.data.liked);
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Failed to update favorite');
    }
  };

  const handleToggleFollow = async () => {
    if (!user) {
      toast.error('Please login to follow services');
      return;
    }
    const token = localStorage.getItem('session_token');
    try {
      const response = await axios.post(
        `${API}/tipster-services/${serviceId}/follow`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFollowing(response.data.is_following);
      toast.success(response.data.message);
      
      // Track follow/unfollow event
      if (response.data.is_following) {
        trackEvent('service_followed', 'Tipster', service?.name || serviceId);
      } else {
        trackEvent('service_unfollowed', 'Tipster', service?.name || serviceId);
      }
    } catch (error) {
      toast.error('Failed to update follow status');
    }
  };

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/tipster-services/${serviceId}/posts`);
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size
      const maxSize = file.type.startsWith('video/') ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
      if (file.size > maxSize) {
        toast.error(`File too large. Max size: ${maxSize / 1024 / 1024}MB`);
        return;
      }
      setUploadData({ ...uploadData, file });
    }
  };

  const handleUpload = async () => {
    if (!uploadData.file) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    const token = localStorage.getItem('session_token');

    try {
      // Upload file
      const formData = new FormData();
      formData.append('file', uploadData.file);

      const uploadResponse = await axios.post(`${API}/upload`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      // Create post with uploaded file URL
      await axios.post(
        `${API}/tipster-services/${serviceId}/posts`,
        {
          content: uploadData.content,
          media_url: uploadResponse.data.url,
          media_type: uploadResponse.data.media_type
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Post uploaded successfully!');
      setUploadDialogOpen(false);
      setUploadData({ content: '', file: null });
      fetchPosts();
    } catch (error) {
      console.error('Error uploading:', error);
      toast.error('Failed to upload post');
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    const token = localStorage.getItem('session_token');
    try {
      await axios.delete(`${API}/tipster-services/${serviceId}/posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Post deleted successfully');
      fetchPosts();
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error('Failed to delete post');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-white text-lg">Loading service details...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!service) return null;

  const isOwner = user && service.user_id === user.id;

  const getSocialIcon = (platform) => {
    const icons = {
      facebook: Facebook,
      instagram: Instagram,
      twitter: Twitter
    };
    return icons[platform.toLowerCase()] || Globe;
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4 sm:p-6 lg:p-8">
        <div className="max-w-6xl mx-auto">
          <Button onClick={() => navigate('/tip-services')} variant="ghost" className="text-white mb-6">
            ← Back to Tip Services
          </Button>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Service Info */}
            <div className="lg:col-span-1">
              <Card className="glass-dark border-white/10">
                <CardContent className="pt-6">
                  {service.image_url && (
                    <div className="mb-6 rounded-lg overflow-hidden">
                      <img src={service.image_url} alt={service.name} className="w-full h-48 object-cover" />
                    </div>
                  )}

                  <div className="flex items-start justify-between mb-2">
                    <h1 className="text-2xl font-bold text-white">{service.name}</h1>
                    <div className="flex gap-2">
                      <Button
                        onClick={handleToggleFollow}
                        size="sm"
                        className={`${following ? 'bg-blue-600 hover:bg-blue-700' : 'bg-white/5 hover:bg-white/10 border border-white/20'} text-white`}
                      >
                        {following ? (
                          <>
                            <Bell className="w-4 h-4 mr-1" />
                            Following
                          </>
                        ) : (
                          <>
                            <BellOff className="w-4 h-4 mr-1" />
                            Follow
                          </>
                        )}
                      </Button>
                      <Button
                        onClick={handleToggleLike}
                        variant="ghost"
                        size="sm"
                        className={`${liked ? 'text-red-400' : 'text-gray-400'} hover:text-red-400 p-1`}
                      >
                        <Heart className={`w-6 h-6 ${liked ? 'fill-red-400' : ''}`} />
                      </Button>
                    </div>
                  </div>

                  {/* Rating Display */}
                  {totalReviews > 0 && (
                    <div className="flex items-center space-x-2 mb-4">
                      <div className="flex items-center">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className={`w-5 h-5 ${
                              star <= Math.round(averageRating)
                                ? 'fill-yellow-400 text-yellow-400'
                                : 'text-gray-600'
                            }`}
                          />
                        ))}
                      </div>
                      <span className="text-white font-medium">{averageRating}</span>
                      <span className="text-gray-400">({totalReviews} {totalReviews === 1 ? 'review' : 'reviews'})</span>
                    </div>
                  )}

                  <p className="text-lg text-blue-400 mb-4">{service.title}</p>

                  {service.subscription_price > 0 ? (
                    <div className="glass p-4 rounded-lg mb-4">
                      <p className="text-sm text-gray-400">Monthly Subscription</p>
                      <p className="text-xl font-bold text-green-400">
                        <Price amount={service.subscription_price} />/month
                      </p>
                    </div>
                  ) : (
                    <div className="glass p-4 rounded-lg mb-4">
                      <p className="text-sm text-gray-400">Access</p>
                      <p className="text-xl font-bold text-green-400">Free</p>
                    </div>
                  )}

                  <div className="mb-4">
                    <h2 className="text-lg font-bold text-white mb-2">About</h2>
                    <p className="text-gray-300 text-sm whitespace-pre-wrap">{service.description}</p>
                  </div>

                  <div className="space-y-3">
                    <h2 className="text-lg font-bold text-white">Contact</h2>
                    
                    <a href={`mailto:${service.email}`} className="glass p-3 rounded-lg flex items-center hover:bg-white/10 transition-colors text-sm">
                      <Mail className="w-4 h-4 text-blue-400 mr-2" />
                      <span className="text-white truncate">{service.email}</span>
                    </a>

                    {service.telephone && (
                      <a href={`tel:${service.telephone}`} className="glass p-3 rounded-lg flex items-center hover:bg-white/10 transition-colors text-sm">
                        <Phone className="w-4 h-4 text-blue-400 mr-2" />
                        <span className="text-white">{service.telephone}</span>
                      </a>
                    )}

                    {service.website && (
                      <a href={service.website} target="_blank" rel="noopener noreferrer" className="glass p-3 rounded-lg flex items-center hover:bg-white/10 transition-colors text-sm">
                        <Globe className="w-4 h-4 text-blue-400 mr-2" />
                        <span className="text-white truncate">{service.website}</span>
                      </a>
                    )}

                    {service.social_networks && Object.keys(service.social_networks).length > 0 && (
                      <div className="mt-4">
                        <h3 className="text-sm font-bold text-white mb-2">Social Networks</h3>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(service.social_networks).map(([platform, url]) => {
                            const Icon = getSocialIcon(platform);
                            return (
                              <a
                                key={platform}
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="glass p-2 rounded-lg hover:bg-white/10 transition-colors"
                                title={platform}
                              >
                                <Icon className="w-5 h-5 text-blue-400" />
                              </a>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Main Content Area - Tips & Posts */}
            <div className="lg:col-span-2">
              {/* Statistics Section */}
              <ServiceStats serviceId={serviceId} serviceName={service?.name} showShareButton={true} />
              
              {/* Subscription Button */}
              {!isOwner && service && (
                <SubscriptionButton service={service} />
              )}
              
              {/* Tips Section */}
              <div className="mb-8">
                {isOwner && (
                  <div className="mb-8">
                    <CreateTipForm serviceId={serviceId} onTipCreated={() => setTipsRefresh(prev => prev + 1)} />
                  </div>
                )}
                
                <TipsTable key={tipsRefresh} serviceId={serviceId} isOwnService={isOwner} />
              </div>

              {/* Posts Gallery */}
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">Posts & Media</h2>
                {isOwner && (
                  <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
                    <DialogTrigger asChild>
                      <Button className="bg-blue-600 hover:bg-blue-700">
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Post
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-slate-900 border-white/10">
                      <DialogHeader>
                        <DialogTitle className="text-white">Upload New Post</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        <div>
                          <Label htmlFor="file" className="text-white">Image or Video *</Label>
                          <Input
                            id="file"
                            type="file"
                            accept="image/*,video/*"
                            onChange={handleFileChange}
                            className="bg-white/5 border-white/10 text-white mt-2"
                          />
                          <p className="text-xs text-gray-400 mt-1">Max 10MB for images, 50MB for videos</p>
                        </div>
                        <div>
                          <Label htmlFor="content" className="text-white">Caption (optional)</Label>
                          <Textarea
                            id="content"
                            value={uploadData.content}
                            onChange={(e) => setUploadData({ ...uploadData, content: e.target.value })}
                            className="bg-white/5 border-white/10 text-white mt-2"
                            placeholder="Add a caption for your post..."
                            rows={3}
                          />
                        </div>
                        <Button
                          onClick={handleUpload}
                          disabled={uploading}
                          className="w-full bg-blue-600 hover:bg-blue-700"
                        >
                          {uploading ? 'Uploading...' : 'Upload'}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                )}
              </div>

              {posts.length === 0 ? (
                <Card className="glass-dark border-white/10">
                  <CardContent className="pt-6 text-center text-gray-400">
                    <ImageIcon className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>No posts yet</p>
                    {isOwner && <p className="text-sm mt-2">Upload your first post to showcase your service!</p>}
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {posts.map((post) => (
                    <Card key={post.id} className="glass-dark border-white/10 overflow-hidden">
                      <div className="relative aspect-video bg-slate-800">
                        {post.media_type === 'image' ? (
                          <img
                            src={`${BACKEND_URL}${post.media_url}`}
                            alt="Post"
                            className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                            onClick={() => setSelectedImage(`${BACKEND_URL}${post.media_url}`)}
                            onError={(e) => {
                              console.error('Image failed to load:', `${BACKEND_URL}${post.media_url}`);
                              e.target.src = 'https://via.placeholder.com/400x300?text=Image+Not+Found';
                            }}
                          />
                        ) : (
                          <video
                            controls
                            controlsList="nodownload"
                            preload="metadata"
                            playsInline
                            className="w-full h-full object-cover bg-black"
                            style={{ maxHeight: '100%' }}
                            onLoadedMetadata={() => console.log('Video metadata loaded')}
                            onError={(e) => {
                              console.error('Video failed to load:', `${BACKEND_URL}${post.media_url}`);
                              console.error('Video error details:', e.target.error);
                            }}
                          >
                            <source src={`${BACKEND_URL}${post.media_url}`} type="video/mp4" />
                            <source src={`${BACKEND_URL}${post.media_url}`} type="video/quicktime" />
                            Your browser does not support the video tag.
                          </video>
                        )}
                        {isOwner && (
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleDeletePost(post.id)}
                            className="absolute top-2 right-2 bg-red-600 hover:bg-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      {post.content && (
                        <CardContent className="pt-4">
                          <p className="text-gray-300 text-sm">{post.content}</p>
                        </CardContent>
                      )}
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Reviews Section */}
          <Card className="glass-dark border-white/10 mt-6">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <Star className="w-5 h-5 mr-2 text-yellow-400" />
                Reviews ({totalReviews})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {user ? (
                <div>
                  <h3 className="text-white font-medium mb-4">Write a Review</h3>
                  <ReviewForm
                    itemId={serviceId}
                    itemType="tipster_service"
                    onReviewSubmitted={fetchReviews}
                  />
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-gray-400 mb-3">Please login to write a review</p>
                  <Button
                    onClick={() => navigate('/login')}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Login
                  </Button>
                </div>
              )}

              <div className="border-t border-white/10 pt-6">
                <h3 className="text-white font-medium mb-4">User Reviews</h3>
                <ReviewsList
                  reviews={reviews}
                  currentUserId={user?.id}
                  isAdmin={user?.is_admin}
                  onReviewDeleted={fetchReviews}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Image Lightbox Modal */}
        <Dialog open={selectedImage !== null} onOpenChange={(open) => !open && setSelectedImage(null)}>
          <DialogContent className="max-w-6xl w-full bg-slate-900/95 border-white/10 p-0">
            <div className="relative">
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute top-4 right-4 z-10 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
                aria-label="Close"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
              {selectedImage && (
                <img
                  src={selectedImage}
                  alt="Full size"
                  className="w-full h-auto max-h-[90vh] object-contain"
                  onClick={() => setSelectedImage(null)}
                />
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default ServiceDetail;
